import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useClientSubscriptionStore } from "@/store/api/clients/clientSubscriptionStore";
import { useUserStore } from "@/store/userStore";
import { useSubscriptionCalendarLabels } from "@/hooks/useSubscriptionCalendarLabels";
import { ChevronRight, Crown, Sparkles, AlertCircle } from "lucide-react";
import { differenceInDays, parseISO, format } from "date-fns";
import { fr } from "date-fns/locale";
import type { Subscription } from "@/types/subscription";

/**
 * Un adhérent peut détenir plusieurs abonnements actifs, de plannings
 * différents, avec des dates de fin indépendantes. On affiche donc une ligne
 * par abonnement : sans cela, l'échéance du second n'apparaissait nulle part
 * sur le tableau de bord.
 *
 * Avec un unique abonnement, le rendu est celui d'avant : le libellé de
 * planning n'apparaît que si la salle utilise réellement le multi-planning.
 */
export const SubscriptionCreditsCard = () => {
  const { t } = useTranslation(["athlete", "common"]);
  const navigate = useNavigate();
  const { allSubscriptions } = useClientSubscriptionStore();
  const gymId = useUserStore((s) => s.gymId);

  const activeSubscriptions = useMemo(
    () => (allSubscriptions ?? []).filter((s) => s.status === "Active"),
    [allSubscriptions]
  );
  const { calendarLabelFor } = useSubscriptionCalendarLabels(activeSubscriptions, gymId);

  if (activeSubscriptions.length === 0) return null;

  const getSubscriptionIcon = (subscription: Subscription) => {
    if (subscription.type?.toLowerCase().includes("premium")) return Crown;
    return Sparkles;
  };

  return (
    <div className="glass-stat-card athlete-subscription-summary overflow-hidden">
      {activeSubscriptions.map((subscription) => {
        const daysRemaining = subscription.end_date
          ? differenceInDays(parseISO(subscription.end_date), new Date())
          : null;
        const showExpirationWarning =
          daysRemaining !== null && daysRemaining <= 30 && daysRemaining >= 0;
        const isExpired = daysRemaining !== null && daysRemaining < 0;
        const SubscriptionIcon = getSubscriptionIcon(subscription);
        const calendarLabel = calendarLabelFor(subscription.id);
        const total = subscription.sessions_total ?? 0;
        const remaining = subscription.sessions_remaining ?? 0;
        const used = Math.max(total - remaining, 0);
        const progress = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

        return (
          <button
            key={subscription.id}
            type="button"
            onClick={() => navigate("/athlete/subscriptions")}
            aria-label={t('subscriptions.title', { defaultValue: 'Abonnements' })}
            className="relative grid w-full grid-cols-[auto_1fr_auto_auto] items-center gap-3 p-4 text-left transition-colors active:bg-muted/40"
          >
            <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
              <SubscriptionIcon className="h-5 w-5 text-primary" />
            </div>

            <div className="flex min-w-0 flex-col">
              <span className="font-bold text-sm">
                {subscription.type}
                {calendarLabel && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    · {calendarLabel}
                  </span>
                )}
              </span>
              {subscription.end_date && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  {isExpired
                    ? t('subscriptions.expired')
                    : `${t('subscriptions.validUntil', { defaultValue: "Jusqu'au" })} ${format(parseISO(subscription.end_date), 'dd MMMM yyyy', { locale: fr })}`
                  }
                  {(showExpirationWarning || isExpired) && (
                    <AlertCircle className="h-3.5 w-3.5 text-destructive inline" />
                  )}
                </span>
              )}
               {total > 0 && <div className="athlete-progress-track mt-2 h-1.5 overflow-hidden rounded-full bg-muted"><div className="athlete-progress-fill h-full rounded-full" style={{ width: `${progress}%` }} /></div>}
            </div>
            {total > 0 && <div className="text-right"><strong className="text-lg">{used} / {total}</strong><p className="text-[10px] text-muted-foreground">{t('stats.totalClasses')}</p></div>}
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
};
